import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { adminApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { StatusPill } from '../../components/StatusPills';
import SeverityPill, { severityRowClass } from '../../components/admin/SeverityPill';
import { SECURITY_CHANGED_EVENT } from '../../components/admin/SecurityAlertBanner';
import BulkAcknowledgeDialog from '../../components/admin/BulkAcknowledgeDialog';

const PAGE_SIZE = 50;

const SOURCE_LABELS = {
  app: 'App',
  mongo_audit: 'Database audit log',
  auditd: 'Server file audit',
  journald: 'Server system log',
  integrity_check: 'Integrity check',
  internal_endpoint: 'Monitoring endpoint',
  mail: 'Incoming mail',
};

const DETAIL_LABELS = {
  summary: 'What happened',
  db_user: 'Database user',
  remote_ip: 'Connected from',
  namespace: 'Collection',
  command: 'Command',
  exe: 'Program',
  comm: 'Process',
  uid: 'Unix user id',
  auid: 'Logged-in user id',
  audit_key: 'Audit rule',
  path: 'File',
  host: 'Server',
  unit: 'Service',
  problem: 'Problem',
  failed_attempts: 'Failed attempts',
  window_minutes: 'Within (minutes)',
  distinct_accounts: 'Different accounts',
  attempt: 'Attempted',
  observed_at: 'Observed at',
  sender: 'Sent by',
  full: 'Full check',
  checked: 'Entries checked',
  head_seq: 'Last entry',
  event_action: 'Event',
  event_seq: 'Event entry',
  reason: 'Reason',
  method: 'Method',
  route: 'Route',
  status: 'Response',
};

const when = (ts) => (ts ? new Date(ts).toLocaleString('en-GB') : '—');

// =========================================================================
// Page
// =========================================================================
const AdminSecurity = () => {
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState({ results: [], total: 0 });
  const [minSeverity, setMinSeverity] = useState('warning');
  const [openOnly, setOpenOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const loadSummary = useCallback(
    () => adminApi.securitySummary().then((r) => setSummary(r.data)).catch(() => {}),
    [],
  );

  const loadEvents = useCallback(() => {
    setLoading(true);
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
    if (minSeverity !== 'all') params.min_severity = minSeverity;
    if (openOnly) params.unacknowledged_only = true;
    adminApi
      .securityEvents(params)
      .then((r) => setEvents(r.data || { results: [], total: 0 }))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load security events'))
      .finally(() => setLoading(false));
  }, [minSeverity, openOnly, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const refresh = () => {
    loadSummary();
    loadEvents();
    window.dispatchEvent(new Event(SECURITY_CHANGED_EVENT));
  };

  const verify = () => {
    setVerifying(true);
    adminApi
      .verifyAuditChain()
      .then((r) => {
        const res = r.data;
        if (res.ok && !res.loki_problem) toast.success(`Audit log intact — ${res.checked} entries checked.`);
        else toast.error(`Integrity check failed: ${res.problem || res.loki_problem}`);
        refresh();
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Integrity check could not run'))
      .finally(() => setVerifying(false));
  };

  const totalPages = Math.max(1, Math.ceil((events.total || 0) / PAGE_SIZE));

  return (
    <div className="space-y-8" data-testid="admin-security-page">
      <div>
        <h1 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] text-ink">Security</h1>
        <p className="text-ink-70 mt-2 max-w-[68ch]">
          Signals from the app, the database audit log and the server itself. Critical events
          are emailed to every admin as they happen and stay at the top of every admin page
          until someone acknowledges them.
        </p>
      </div>

      {summary && <SummaryTiles summary={summary} />}

      {summary && <ChainStatus chain={summary.chain} onVerify={verify} verifying={verifying} />}

      {summary && summary.unacknowledged_critical?.length > 0 && (
        <section aria-labelledby="open-critical" className="space-y-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 id="open-critical" className="pd-eyebrow">
              Needs reviewing · {summary.unacknowledged_critical_count}
            </h2>
            {summary.unacknowledged_critical_count > 1 && (
              <Button variant="secondary" onClick={() => setBulkOpen(true)} data-testid="bulk-ack-open">
                Acknowledge all…
              </Button>
            )}
          </div>
          {summary.unacknowledged_critical_count > summary.unacknowledged_critical.length && (
            <p className="text-sm text-ink-55">
              Showing the newest {summary.unacknowledged_critical.length}. The rest are in the table
              below, and "Acknowledge all" covers every one.
            </p>
          )}
          <BulkAcknowledgeDialog
            open={bulkOpen}
            onOpenChange={setBulkOpen}
            byAction={summary.unacknowledged_critical_by_action}
            upToSeq={summary.unacknowledged_critical_max_seq}
            onDone={refresh}
          />
          <div className="space-y-3">
            {summary.unacknowledged_critical.map((ev) => (
              <CriticalCard key={ev.id} event={ev} onAcknowledged={refresh} />
            ))}
          </div>
        </section>
      )}

      <section aria-labelledby="all-events" className="space-y-3">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <h2 id="all-events" className="pd-eyebrow">All security events</h2>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={minSeverity} onValueChange={(v) => { setMinSeverity(v); setPage(0); }}>
              <SelectTrigger className="w-[200px]" data-testid="security-severity-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="critical">Critical only</SelectItem>
                <SelectItem value="warning">Warning and critical</SelectItem>
                <SelectItem value="notice">Notice and above</SelectItem>
                <SelectItem value="all">Everything</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={openOnly ? 'default' : 'secondary'}
              onClick={() => { setOpenOnly((v) => !v); setPage(0); }}
              aria-pressed={openOnly}
              data-testid="security-open-only"
            >
              {openOnly ? 'Showing unacknowledged' : 'Only unacknowledged'}
            </Button>
          </div>
        </div>

        <div className="rounded-lg bg-ground overflow-x-auto">
          {loading ? (
            <p className="text-sm text-ink-55 p-6">Loading…</p>
          ) : events.results.length === 0 ? (
            <p className="text-sm text-ink-55 p-6">Nothing matches. Security events appear here as soon as they are recorded.</p>
          ) : (
            <table className="w-full text-sm" data-testid="security-events-table">
              <thead>
                <tr className="text-left text-[13px] text-ink-55">
                  <th className="p-3 font-medium">When</th>
                  <th className="p-3 font-medium">Severity</th>
                  <th className="p-3 font-medium">Event</th>
                  <th className="p-3 font-medium">Source</th>
                  <th className="p-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {events.results.map((ev) => <EventRow key={ev.id} event={ev} onAcknowledged={refresh} />)}
              </tbody>
            </table>
          )}
        </div>

        {events.total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm text-ink-55">
            <span>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, events.total)} of {events.total}</span>
            <div className="flex items-center gap-2">
              <Button variant="secondary" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span>Page {page + 1} of {totalPages}</span>
              <Button variant="secondary" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

// =========================================================================
// Pieces
// =========================================================================
const SummaryTiles = ({ summary }) => {
  const day = summary.last_24h || {};
  const week = summary.last_7d || {};
  const tiles = [
    { key: 'critical', label: 'Critical' },
    { key: 'warning', label: 'Warning' },
    { key: 'notice', label: 'Notice' },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="security-summary">
      {tiles.map((t) => (
        <div key={t.key} className="pd-tile rounded-lg bg-ground p-5">
          <p className="pd-eyebrow">{t.label} · last 24 hours</p>
          <p
            className={`font-heading text-[42px] font-bold tracking-[-0.035em] leading-none mt-3 ${
              t.key === 'critical' && day.critical ? 'text-breach-ink' : t.key === 'warning' && day.warning ? 'text-amber-ink' : 'text-ink'
            }`}
          >
            {day[t.key] || 0}
          </p>
          <p className="text-sm text-ink-55 mt-2">{week[t.key] || 0} in the last 7 days</p>
        </div>
      ))}
    </div>
  );
};

const ChainStatus = ({ chain, onVerify, verifying }) => {
  if (!chain) return null;
  const broken = !!chain.last_problem || !!chain.loki_last_problem;
  return (
    <section
      aria-labelledby="chain-status"
      className={`rounded-lg p-5 flex items-start justify-between gap-4 flex-wrap ${broken ? 'bg-breach-wash' : 'bg-ground'}`}
      data-testid="audit-chain-status"
    >
      <div className="space-y-1.5">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 id="chain-status" className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">
            Audit log integrity
          </h2>
          {broken ? (
            <StatusPill tone="breach" size="sm">Failed</StatusPill>
          ) : chain.last_ok_at ? (
            <StatusPill tone="done" size="sm">Intact</StatusPill>
          ) : (
            <StatusPill tone="neutral" size="sm">Not checked yet</StatusPill>
          )}
        </div>
        {broken ? (
          <p className="text-breach-ink">{chain.last_problem || `Loki cross-check: ${chain.loki_last_problem}`}</p>
        ) : (
          <p className="text-ink-70">
            Every entry is chained to the one before it, so an edit, a deletion or a cut-off end
            shows up here. Checked hourly; a full check runs daily.
          </p>
        )}
        <p className="pd-mono text-ink-55">
          {chain.head_seq != null ? `${chain.head_seq + 1} entries` : 'No entries yet'}
          {chain.last_ok_at ? ` · last intact ${when(chain.last_ok_at)}` : ''}
          {chain.loki_configured ? '' : ' · Loki cross-check off'}
          {chain.file_mirror ? '' : ' · file mirror off'}
        </p>
      </div>
      <Button variant="secondary" onClick={onVerify} disabled={verifying} data-testid="verify-chain-btn">
        {verifying ? 'Checking…' : 'Check integrity now'}
      </Button>
    </section>
  );
};

const Details = ({ event }) => {
  const meta = event.metadata || {};
  const rows = Object.entries(meta).filter(([k, v]) => v !== null && v !== '' && k !== 'source' && typeof v !== 'object');
  const actor = event.actor || {};
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-4 gap-y-1.5 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-ink-55">{DETAIL_LABELS[k] || k.replace(/_/g, ' ')}</dt>
          <dd className="text-ink break-words">{String(v)}</dd>
        </div>
      ))}
      {event.ip_address && (
        <div className="contents">
          <dt className="text-ink-55">IP address</dt>
          <dd className="pd-mono text-ink">{event.ip_address}</dd>
        </div>
      )}
      {actor.type && actor.type !== 'external' && (
        <div className="contents">
          <dt className="text-ink-55">Actor</dt>
          <dd className="text-ink">{actor.type}{actor.user_id ? ` · ${actor.user_id}` : ''}</dd>
        </div>
      )}
      <div className="contents">
        <dt className="text-ink-55">Audit entry</dt>
        <dd className="pd-mono text-ink">#{event.seq} · {event.hash ? event.hash.slice(0, 16) : ''}</dd>
      </div>
      {event.user_id && (
        <div className="contents">
          <dt className="text-ink-55">Account</dt>
          <dd>
            <Link className="text-brand underline" to={`/admin/activity-logs/users/${event.user_id}`}>
              Open this account's activity
            </Link>
          </dd>
        </div>
      )}
    </dl>
  );
};

const AcknowledgeForm = ({ event, onDone, compact = false }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = () => {
    setSaving(true);
    adminApi
      .acknowledgeSecurityEvent(event.id, note.trim())
      .then(() => { toast.success('Acknowledged.'); onDone(); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not acknowledge'))
      .finally(() => setSaving(false));
  };
  return (
    <div className={`space-y-2 ${compact ? '' : 'pt-2'}`}>
      <label htmlFor={`ack-${event.id}`} className="text-[15px] font-semibold text-ink block">
        What did you find?
      </label>
      <p className="text-sm text-ink-55">Optional. Saved with your name alongside the event — the event itself is never changed.</p>
      <Textarea
        id={`ack-${event.id}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={1000}
        rows={2}
        placeholder="e.g. This was me running provision.sh"
      />
      <Button onClick={submit} disabled={saving} data-testid={`ack-${event.id}`}>
        {saving ? 'Saving…' : 'Acknowledge'}
      </Button>
    </div>
  );
};

const CriticalCard = ({ event, onAcknowledged }) => (
  <article className="rounded-lg bg-breach-wash p-5 space-y-4" data-testid={`critical-${event.id}`}>
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <p className="pd-mono text-breach-ink">{when(event.timestamp)} · {SOURCE_LABELS[event.metadata?.source] || event.metadata?.source || 'App'}</p>
        <h3 className="font-heading text-[21px] font-semibold tracking-[-0.02em] text-ink mt-1">{event.label}</h3>
      </div>
      <SeverityPill severity="critical" size="md" />
    </div>
    <Details event={event} />
    <AcknowledgeForm event={event} onDone={onAcknowledged} />
  </article>
);

const EventRow = ({ event, onAcknowledged }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className={`border-t border-rule align-top ${severityRowClass(event.severity)}`}>
        <td className="p-3 whitespace-nowrap pd-mono text-ink-55">{when(event.timestamp)}</td>
        <td className="p-3"><SeverityPill severity={event.severity} /></td>
        <td className="p-3">
          <button
            type="button"
            className="text-left text-ink font-medium hover:underline"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {event.label}
          </button>
        </td>
        <td className="p-3 text-ink-70">{SOURCE_LABELS[event.metadata?.source] || event.metadata?.source || 'App'}</td>
        <td className="p-3">
          {event.acknowledged ? (
            <StatusPill tone="done" size="sm" title={event.acknowledgement_note || undefined}>
              Acknowledged
            </StatusPill>
          ) : event.severity === 'critical' ? (
            <StatusPill tone="breach" size="sm">Open</StatusPill>
          ) : (
            <span className="text-ink-55">—</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className={severityRowClass(event.severity)}>
          <td colSpan={5} className="px-3 pb-4">
            <div className="rounded-lg bg-surface p-4 space-y-3">
              <Details event={event} />
              {event.acknowledged ? (
                <p className="text-sm text-ink-55">
                  Acknowledged by {event.acknowledged_by_email || 'an admin'} on {when(event.acknowledged_at)}
                  {event.acknowledgement_note ? ` — “${event.acknowledgement_note}”` : ''}
                </p>
              ) : (
                <AcknowledgeForm event={event} onDone={onAcknowledged} compact />
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default AdminSecurity;
